import React, { useEffect, useState } from "react";
import { FaIndustry, FaRupeeSign, FaBriefcase, FaCheckCircle, FaArrowLeft } from "react-icons/fa";
import { FcGraduationCap } from "react-icons/fc";
import { GoLocation } from "react-icons/go";
import { PiBagSimple } from "react-icons/pi";
import { ImEye } from "react-icons/im";
import { FaRegBookmark, FaBookmark } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  CreateMyJobs,
  GetOneJob,
  GetMyJobs,
  ViewsOnJob,
} from "../../../Redux/Slices/JobPost";
import {
  FacebookIcon,
  FacebookShareButton,
  TwitterIcon,
  TwitterShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  TelegramIcon,
  TelegramShareButton,
} from "react-share";
import { GetUser } from "../../../Redux/Slices/User";
import toast from "react-hot-toast";
import { formatTimeAgo } from "../../../Helper/FromatTime";
import { getJobShareUrl } from "../../../Helper/shareUrl";
import { needsJobseekerOnboarding } from "../../../Helper/jobseekerRedirect";
import { formatJobLocation } from "../../../Helper/CoreHelper";
import "./JobDescription.css";
import SeoHead from "../../Common/SeoHead";
import { buildJobPostingJsonLd, truncateText } from "../../../Helper/structuredData";

const QuickApplyModal = ({ show, onClose, onConfirm, job, isApplying }) => {
  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-icon">
          <FaBriefcase />
        </div>
        <h2 className="modal-title">Confirm Application</h2>
        <p className="modal-message">
          You're about to apply for this position. Your profile and resume will be shared with the employer.
        </p>
        <div className="modal-job-info">
          <h3 className="modal-job-title">{job?.title}</h3>
          <p className="modal-company-name">{job?.company_name}</p>
        </div>
        <div className="modal-actions">
          <button
            className="modal-btn modal-btn-cancel"
            onClick={onClose}
            disabled={isApplying}
          >
            Cancel
          </button>
          <button
            className="modal-btn modal-btn-confirm"
            onClick={onConfirm}
            disabled={isApplying}
          >
            {isApplying ? (
              <>
                <span className="loading-spinner"></span>
                <span style={{ marginLeft: '8px' }}>Applying...</span>
              </>
            ) : (
              'Confirm & Apply'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const JobDescriptionModern = () => {
  const userId = localStorage.getItem("id");
  const role = localStorage.getItem("role");
  const { userData, completionPercentage } = useSelector((state) => state?.User);
  const { jobdetails, similarjobs, myjobs } = useSelector((state) => state?.Jobs);
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const shareUrl = getJobShareUrl(id);
  const shareTitle = jobdetails?.title
    ? `${jobdetails.title} | ${jobdetails.company_name || "CAJobPortal"}`
    : "Job Opening | CAJobPortal";
  const shareSummary = [
    jobdetails?.company_name,
    formatJobLocation(jobdetails),
  ]
    .filter(Boolean)
    .join(" · ");

  const jobLocation = formatJobLocation(jobdetails);

  const [showApplyModal, setShowApplyModal] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isAlreadyApplied, setIsAlreadyApplied] = useState(false);
  const [liveViews, setLiveViews] = useState(null);

  useEffect(() => {
    if (userId) {
      dispatch(GetUser(userId));
      dispatch(GetMyJobs(userId));
    }
    dispatch(GetOneJob(id));
  }, [id, dispatch, userId]);

  useEffect(() => {
    if (!id) return;

    setLiveViews(null);
    let cancelled = false;

    ViewsOnJob({ id }).then((result) => {
      if (!cancelled && typeof result?.views === "number") {
        setLiveViews(result.views);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [id]);

  // Check if job is already applied or saved
  useEffect(() => {
    if (myjobs && jobdetails) {
      const appliedJob = myjobs.find(
        (job) => job.job_id?._id === jobdetails._id && job.type === "applied"
      );
      const savedJob = myjobs.find(
        (job) => job.job_id?._id === jobdetails._id && job.type === "saved"
      );
      setIsAlreadyApplied(!!appliedJob);
      setIsSaved(!!savedJob);
    }
  }, [myjobs, jobdetails]);

  const resumeUploadPath = needsJobseekerOnboarding(userData, completionPercentage)
    ? "/jobseeker/onboarding"
    : "/jobseeker/profile";

  const handleQuickApply = () => {
    if (!userId || role !== "Jobseeker") {
      toast.error("Please login as a Job Seeker to apply");
      navigate("/signin");
      return;
    }

    if (!userData?.resume) {
      toast.error("Please upload your resume first");
      navigate(resumeUploadPath);
      return;
    }

    setShowApplyModal(true);
  };

  const confirmApplication = async () => {
    setIsApplying(true);
    
    try {
      const data = {
        type: "applied",
        jobid: jobdetails._id,
      };
      
      await CreateMyJobs(userId, data);
      
      // Open external form if exists
      if (jobdetails?.form_link) {
        window.open(jobdetails.form_link, "_blank");
      }
      
      setIsAlreadyApplied(true);
      setShowApplyModal(false);
      toast.success("Application submitted successfully!");
      dispatch(GetMyJobs(userId));
    } catch (error) {
      toast.error("Failed to submit application");
    } finally {
      setIsApplying(false);
    }
  };

  const handleSaveJob = async () => {
    if (!userId || role !== "Jobseeker") {
      toast.error("Please login as a Job Seeker to save jobs");
      navigate("/signin");
      return;
    }

    try {
      const data = {
        type: isSaved ? "unsaved" : "saved",
        jobid: jobdetails._id,
      };
      
      await CreateMyJobs(userId, data);
      setIsSaved(!isSaved);
      toast.success(isSaved ? "Job removed from saved" : "Job saved successfully!");
      dispatch(GetMyJobs(userId));
    } catch (error) {
      toast.error("Failed to save job");
    }
  };

  const viewSimilarJob = (jobId) => {
    navigate(`/job-description/${jobId}`);
  };

  const qualificationNames = jobdetails?.qualification
    ?.map((value) => value.name)
    .join(", ");
  // Prefer the live increment so guests see the count update immediately.
  const displayedViews = liveViews ?? jobdetails?.views ?? 0;

  return (
    <div className="job-description-page">
      {jobdetails && (
        <SeoHead
          title={shareTitle}
          description={truncateText(
            jobdetails.description ||
              shareSummary ||
              `${jobdetails.title} opening on CAJobPortal for CA, CS, and CMA professionals.`,
            160
          )}
          path={`/job-description/${id}`}
          breadcrumbs={[
            { name: "Home", path: "/" },
            { name: "Jobs", path: "/jobs" },
            { name: jobdetails.title || "Job Details", path: `/job-description/${id}` },
          ]}
          jsonLd={[buildJobPostingJsonLd(jobdetails)].filter(Boolean)}
        />
      )}
      <div className="job-description-container">
        {/* Main Content */}
        <div className="job-main-content">
          {/* Job Header */}
          <div className="job-header">
            <button className="back-btn" onClick={() => navigate(-1)}>
              <FaArrowLeft />
              <span>Back</span>
            </button>
            <h1 className="job-title">{jobdetails?.title || "Job Title"}</h1>
            <p className="company-name">{jobdetails?.company_name || "Company Name"}</p>

            <div className="job-meta">
              {jobdetails?.exp && jobdetails.exp.from !== null && jobdetails.exp.from !== undefined && 
               jobdetails.exp.to !== null && jobdetails.exp.to !== undefined && (
                <div className="meta-item">
                  <PiBagSimple />
                  <span>
                    {jobdetails.exp.from} - {jobdetails.exp.to} Years
                  </span>
                </div>
              )}
              {jobdetails?.ctc && jobdetails.ctc.from_lac !== null && jobdetails.ctc.from_lac !== undefined && 
               jobdetails.ctc.to_lac !== null && jobdetails.ctc.to_lac !== undefined && (
                <div className="meta-item">
                  <FaRupeeSign />
                  <span>
                    {jobdetails.ctc.from_lac}.{jobdetails.ctc.from_thousand || 0} - {jobdetails.ctc.to_lac}.{jobdetails.ctc.to_thousand || 0} LPA
                  </span>
                </div>
              )}
              {jobLocation && (
                <div className="meta-item">
                  <GoLocation />
                  <span>{jobLocation}</span>
                </div>
              )}
              {jobdetails?.industry?.name && (
                <div className="meta-item">
                  <FaIndustry />
                  <span>{jobdetails.industry.name}</span>
                </div>
              )}
            </div>

            <div className="job-badges">
              {jobdetails?.qualification?.length > 0 && (
                <span className="qualification-badge">
                  <FcGraduationCap />
                  {qualificationNames}
                </span>
              )}
              {jobdetails?.category && (
                <span className="category-badge">{jobdetails.category}</span>
              )}
              {jobdetails?.admin_approval_date && (
                <span className="posted-badge">
                  {formatTimeAgo(new Date(jobdetails.admin_approval_date))}
                </span>
              )}
            </div>
          </div>

          {/* Job Description */}
          <div className="job-section">
            <h2 className="section-title">Job Description</h2>
            <div
              className="section-content"
              dangerouslySetInnerHTML={{ __html: jobdetails?.description || "No description available" }}
            />
          </div>

          {/* Company Details */}
          {jobdetails?.about_company && (
            <div className="job-section">
              <h2 className="section-title">About Company</h2>
              <div className="section-content">
                <p>{jobdetails.about_company}</p>
              </div>
            </div>
          )}

          {/* Posted By */}
          {jobdetails?.created_by && (
            <div className="job-section">
              <h2 className="section-title">Posted By</h2>
              <div className="section-content">
                {jobdetails.created_by.name === "Admin" ? (
                  <p>{jobdetails.created_by.name}</p>
                ) : (
                  <p>
                    {jobdetails.created_by.name} at {jobdetails.company_name}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Share Job */}
          <div className="job-section">
            <h2 className="section-title">Share This Job</h2>
            <div className="share-buttons">
              <FacebookShareButton url={shareUrl}>
                <FacebookIcon size={36} round />
              </FacebookShareButton>
              <WhatsappShareButton url={shareUrl}>
                <WhatsappIcon size={36} round />
              </WhatsappShareButton>
              <TwitterShareButton url={shareUrl}>
                <TwitterIcon size={36} round />
              </TwitterShareButton>
              <LinkedinShareButton url={shareUrl} title={shareTitle} summary={shareSummary}>
                <LinkedinIcon size={36} round />
              </LinkedinShareButton>
              <TelegramShareButton url={shareUrl}>
                <TelegramIcon size={36} round />
              </TelegramShareButton>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="job-sidebar">
          {/* Action Card */}
          <div className="action-card">
            {!userId ? (
              /* Not Logged In */
              <div className="login-prompt">
                <p>Login to apply for this job</p>
                <button
                  className="login-btn"
                  onClick={() => navigate("/signin")}
                >
                  Login as Job Seeker
                </button>
              </div>
            ) : role !== "Jobseeker" ? (
              /* Wrong Role */
              <div className="login-prompt">
                <p>Only job seekers can apply</p>
                <button
                  className="login-btn"
                  onClick={() => navigate("/signin")}
                >
                  Login as Job Seeker
                </button>
              </div>
            ) : !userData?.resume ? (
              /* No Resume */
              <div className="resume-prompt">
                <p>Upload your resume to apply</p>
                <button
                  className="upload-resume-btn"
                  onClick={() => navigate(resumeUploadPath)}
                >
                  Upload Resume
                </button>
              </div>
            ) : null}

            {/* Action Buttons */}
            {userId && role === "Jobseeker" && (
              <div className="action-buttons">
                <button
                  className={`apply-btn ${isAlreadyApplied ? 'applied' : ''}`}
                  onClick={handleQuickApply}
                  disabled={isAlreadyApplied || !userData?.resume}
                >
                  {isAlreadyApplied ? (
                    <>
                      <FaCheckCircle />
                      Already Applied
                    </>
                  ) : (
                    'Apply Now'
                  )}
                </button>
                <button
                  className={`save-btn ${isSaved ? 'saved' : ''}`}
                  onClick={handleSaveJob}
                >
                  {isSaved ? (
                    <>
                      <FaBookmark />
                      Saved
                    </>
                  ) : (
                    <>
                      <FaRegBookmark />
                      Save Job
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Analytics Card */}
          <div className="analytics-card">
            <h3 className="analytics-title">Job Analytics</h3>
            <div className="analytics-stats">
              <div className="stat-item">
                <ImEye />
                <span>{displayedViews} Views</span>
              </div>
              <div className="stat-item">
                <FaBriefcase />
                <span>{jobdetails?.applications?.length || 0} Applicants</span>
              </div>
            </div>
          </div>

          {/* Similar Jobs Card */}
          {similarjobs?.length > 0 && (
            <div className="similar-jobs-card">
              <h3 className="similar-jobs-title">Similar Jobs</h3>
              {similarjobs.slice(0, 5).map((job, index) => (
                <div
                  key={index}
                  className="similar-job-item"
                  onClick={() => viewSimilarJob(job._id)}
                >
                  <h4 className="similar-job-title">{job?.title}</h4>
                  <p className="similar-job-industry">{job?.industry?.name || "N/A"}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Apply Modal */}
      <QuickApplyModal
        show={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        onConfirm={confirmApplication}
        job={jobdetails}
        isApplying={isApplying}
      />
    </div>
  );
};

export default JobDescriptionModern;
